<template>
  <div>
    <RecommendedProductsContainer
        :refData="refData"
        :hospitalNeeds="hospitalNeeds"
        :extrasNeeds="extrasNeeds"
        :flexiFeatures="flexiFeatures"
        :promotionalTags="promotionalTags"
        :fullRecommendationList="fullRecommendationList"
        :needAnalysisData="needAnalysisData"
        :isApiFail="isApiFail"
        :isApiLoading="isApiLoading"
        :isErrorMessageShow="noRecommendationsFound"
        :excessValues="excessValues"
        @excessValuesUpdated="updateChosenExcessValues"
      >
        <NoRecommendationsPage></NoRecommendationsPage>
      </RecommendedProductsContainer>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { getUniqueProductGuides } from '@general/js/utils';
import { CoverTypeValue, ProductType } from '@general/js/enums';
import { getCurrentDeviceLayout } from '@/utils';
import DeviceTypes from '@/enums/DeviceTypes';
import ChangeCoverMixins from '../ChangeCoverMixins';
import RecommendedProductsContainer from '../QuotesView/RecommendedProductsContainer.vue';
import NoRecommendationsPage from '@general/js/components/NoRecommendationsPage.vue';

export default {
  name: 'MyRecommendations',

  mixins: [ChangeCoverMixins],

  components: {
    RecommendedProductsContainer,
    NoRecommendationsPage
  },

  props: {
    refData: {
      type: Object,
      required: true,
    },

    isTablet: {
      type: Boolean,
      default: false,
    },

    apiStatus: {
      type: Object,
      required: true,
    },

    fullRecommendationList: {
      type: Array,
      required: true,
    },

    needAnalysisData: {
      type: Object,
      required: true,
    },

    hospitalNeeds: {
      type: Array,
      required: true,
    },

    extrasNeeds: {
      type: Array,
      required: true,
    },

    flexiFeatures: {
      type: Array,
      required: true,
    },

    maxQuoteNum: {
      type: Number,
      default: 3,
    },

    excessValues: {
      type: Array,
    },
  },

  data() {
    return {
      isTableOpen: false,
      quoteData: null,
      currentDevice: null,
      ProductType,
      CoverTypeValue,
      DeviceTypes,
    };
  },

  beforeMount() {
    // initialism current device layout
    this.resizeHandler();
  },

  mounted() {
    window.addEventListener('resize', this.resizeHandler);
  },

  unmounted() {
    window.removeEventListener('resize', this.resizeHandler);
  },

  computed: {
    isApiFail() {
      return this.apiStatus.getMyRecommendations.fail || this.apiStatus.getPrice.fail;
    },

    isApiLoading() {
      return this.apiStatus.getMyRecommendations.loading || this.apiStatus.getPrice.loading;
    },

    promotionalTags() {
      return this.referenceData.recommendationsPromotionalTags;
    },

    noRecommendationsFound() {
      // For when there is only one item in fullRecommendationList (only has current cover and no recommendations)
      return this.fullRecommendationList && this.fullRecommendationList.length <= 1;
    },
  },

  methods: {
    resizeHandler() {
      // This is to fetch the current device layout so that appropriate version of my recommendation is shown
      this.currentDevice = getCurrentDeviceLayout(window.innerWidth);
    },

    /**
     * Allows for PriceFactorStatement to jump to correct step when a value is clicked
     */
    jumpToField(step) {
      this.$parent.updateStep(step);
    },

    getFeature(fullRecommendationList, type, featureName, recommendationIndex) {
      let val = 'Excluded';

      fullRecommendationList[recommendationIndex].products.forEach((product) => {
        if (product.productType.toLowerCase() === type.toLowerCase()) {
          const matchingFeature = product.productProperties.find((feature) => {
            return feature.propertyName.toLowerCase() === featureName.toLowerCase();
          });

          if (matchingFeature) {
            val = matchingFeature.propertyValue;
          }
        } else if (product.productType.toLowerCase() === ProductType.PACKAGE) {
          product.productProperties.forEach((feature) => {
            if (feature.propertyType.toLowerCase() === type.toLowerCase() && feature.propertyName === featureName) {
              val = feature.propertyValue;
            }
          });
        }
      });

      return val;
    },

    getUniqueProducts(recommendationList, index, combinedName) {
      let recommendations = cloneDeep(recommendationList);
      recommendations = recommendations.map((recommendation) => {
        const newProducts = recommendation.products.map((product) => {
          return {
            ...product,
            guideUrl: product.productGuideLink,
            name: product.productName,
          };
        });
        return {
          ...recommendation,
          products: newProducts,
        };
      });

      return getUniqueProductGuides(recommendations[index].products, combinedName);
    },

    hasHospitalFeatures(recommendation) {
      return recommendation.coverType === CoverTypeValue.HOSPITAL_ONLY || recommendation.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS;
    },

    hasExtrasFeatures(recommendation) {
      return recommendation.coverType === CoverTypeValue.EXTRAS_ONLY || recommendation.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS;
    },

    updateStep(targetPageName) {
      this.$emit('navigatePage', targetPageName);
    },

    updateChosenExcessValues(excessValues) {
      this.$emit('excessValuesUpdated', excessValues);
    },

    updateChosenProduct(data) {
      // Save selected products and excess into vuex
      this.updateSelectedProducts(data.selectedProducts);
      this.updateSelectedExcess(data.selectedExcess);

      this.redirectToQAPage();
    },

    /*
     * This function checks the state (open or closed) for the referenced accordion component
     * and updates the watcher accordingly for other components to get if any accordion component is open
     *
     */

    updateAccordionState() {
      const tableAccordions = [];
      const ref = this.$refs;

      if (ref.hospitalFeature) {
        tableAccordions.push(ref.hospitalFeature.accordionState);
      }

      if (ref.extraFeature) {
        tableAccordions.push(ref.extraFeature.accordionState);
      }

      this.isTableOpen = tableAccordions.some((bool) => {
        return bool === true;
      });
    },
  },
};
</script>
